export interface RuleContent {
  type: 'text' | 'list' | 'note';
  content: string | string[];
}

export interface RuleSection {
  title: string;
  content: RuleContent[];
}

export interface GlossaryItem {
  term: string;
  description: string;
}

export const detailedRulesData: Record<string, RuleSection[]> = {
  basic: [
    {
      title: 'ゲームの目的',
      content: [
        { type: 'text', content: '相手のライフポイント（LP）を0にすることで勝利します。' },
      ],
    },
    {
      title: '基本数値',
      content: [
        {
          type: 'list',
          content: [
            '初期ライフポイント: 6000',
            'デッキ枚数: 40枚',
            '同一カード上限: 3枚まで',
            '禁忌カード上限: デッキに1枚まで',
            '初期手札: 5枚',
          ],
        },
      ],
    },
    {
      title: 'SPトークン',
      content: [
        {
          type: 'list',
          content: [
            '初期SP: 1（後攻は2）',
            '最大SP: 10',
            'ターン開始時に+1',
            'アクティブ状態（◆）: 使用可能',
            'レスト状態（◇）: 使用済み、次ターン開始で回復',
          ],
        },
        {
          type: 'note',
          content: 'カードを使うにはコスト分のアクティブSPが必要です。SPが足りないとカードは使えません。',
        },
      ],
    },
  ],

  phase: [
    {
      title: 'ターン開始フェイズ',
      content: [
        {
          type: 'list',
          content: [
            'SPトークンを1個追加（最大10）',
            'レスト状態のSPを全てアクティブに',
          ],
        },
      ],
    },
    {
      title: 'ドローフェイズ',
      content: [
        {
          type: 'list',
          content: [
            'デッキから1枚ドロー',
            'SP1で追加ドロー可能（先攻1ターン目は不可）',
          ],
        },
      ],
    },
    {
      title: 'メインフェイズ',
      content: [
        {
          type: 'list',
          content: [
            'モンスター召喚（コスト分のSP消費）',
            '魔法カード発動',
            'フィールドカード設置',
            'チャージ（1ターン1回）',
            '技発動',
          ],
        },
      ],
    },
    {
      title: 'バトルフェイズ',
      content: [
        {
          type: 'list',
          content: [
            'モンスターで相手を攻撃',
            '相手モンスターがいなければ直接攻撃',
          ],
        },
        { type: 'note', content: '先攻1ターン目は攻撃できません。' },
      ],
    },
    {
      title: 'エンドフェイズ',
      content: [
        { type: 'text', content: 'ターンを終了し、相手にターンを移します。' },
      ],
    },
  ],

  cards: [
    {
      title: 'モンスターカード',
      content: [
        {
          type: 'list',
          content: [
            '攻撃力・HP・効果・カテゴリを持つ',
            'フィールドに最大5体まで配置可能',
            'チャージして技を発動できる',
          ],
        },
      ],
    },
    {
      title: '魔法カード',
      content: [
        {
          type: 'list',
          content: [
            '即時効果を発動し、墓地へ',
            '【刹那詠唱】: 相手ターン中に発動可能',
          ],
        },
      ],
    },
    {
      title: 'フィールドカード',
      content: [
        {
          type: 'list',
          content: [
            '場全体に影響する永続効果',
            '各プレイヤー1枚まで設置可能',
            '直接攻撃ダメージを75%に軽減',
          ],
        },
      ],
    },
    {
      title: 'フェイズカード',
      content: [
        {
          type: 'list',
          content: [
            'SP2で設置',
            '同属性カードをチャージして段階強化（最大3段階）',
            '3段階目発動後、墓地へ送られる',
          ],
        },
      ],
    },
  ],

  battle: [
    {
      title: '基本戦闘',
      content: [
        {
          type: 'list',
          content: [
            '攻撃力分のダメージを相手モンスターのHPに与える',
            'HPが0になったモンスターは破壊',
            '反撃: 被攻撃側の攻撃力の30%をダメージとして受ける',
          ],
        },
      ],
    },
    {
      title: '直接攻撃',
      content: [
        {
          type: 'list',
          content: [
            '相手モンスターがいない場合のみ可能',
            '設置物（フィールド/フェイズカード）があれば75%に軽減',
          ],
        },
        {
          type: 'note',
          content: '一部のカード効果により、相手モンスターがいても直接攻撃可能な場合があります。',
        },
      ],
    },
    {
      title: '孤軍奮闘ルール',
      content: [
        { type: 'text', content: '自分のモンスターが相手より少ない場合、補正を得ます。' },
        {
          type: 'list',
          content: [
            '1体差: 攻撃力+10%、被ダメージ-10%',
            '2体差: 攻撃力+25%、被ダメージ-20%',
            '3体以上差: 攻撃力+40%、被ダメージ-30%',
          ],
        },
      ],
    },
  ],

  skills: [
    {
      title: 'チャージの種類',
      content: [
        {
          type: 'list',
          content: [
            '属性チャージ: カードを重ねる（モンスター破壊まで残る）',
            'SPチャージ: SP1を消費（技発動時に消費）',
          ],
        },
        { type: 'note', content: '1ターンに属性チャージかSPチャージのどちらか1回のみ可能です。' },
      ],
    },
    {
      title: '技の種類',
      content: [
        {
          type: 'list',
          content: [
            '基本技: 1チャージで発動',
            '上級技: 2チャージで発動',
          ],
        },
      ],
    },
    {
      title: '発動条件',
      content: [
        {
          type: 'list',
          content: [
            '各モンスター1ターン1回のみ技発動可能',
            'チャージした属性が技の指定属性と一致する必要あり',
            '「任意」指定の技はどの属性でもOK',
          ],
        },
      ],
    },
    {
      title: '刹那詠唱',
      content: [
        {
          type: 'list',
          content: [
            '相手ターン中に発動可能な魔法カード',
            '通常のコストのみで発動可能',
            'バトルフェイズ開始時、攻撃宣言時に発動可能',
          ],
        },
      ],
    },
  ],

  glossary: [], // 用語集は別で定義
};

export const glossaryData: GlossaryItem[] = [
  { term: 'LP（ライフポイント）', description: 'プレイヤーの生命力。0になると敗北' },
  { term: 'SP（スピリットポイント）', description: 'カード使用に必要な資源' },
  { term: 'アクティブ', description: '使用可能なSP状態（◆）' },
  { term: 'レスト', description: '使用済みのSP状態（◇）' },
  { term: 'チャージ', description: 'カードやSPをモンスターに重ねること' },
  { term: '墓地', description: '使用済み・破壊されたカードの置き場' },
  { term: '禁忌カード', description: 'デッキに1枚しか入れられない強力カード' },
  { term: 'カテゴリ', description: 'モンスターの種族（【ドラゴン】等）' },
  { term: '刹那詠唱', description: '相手ターン中に使える魔法の特性' },
  { term: 'フェイズカード', description: '段階的に強化される特殊カード' },
  { term: '孤軍奮闘', description: 'モンスター数差による補正システム' },
  { term: '設置物', description: 'フィールドカードまたはフェイズカード' },
  { term: '反撃', description: '攻撃を受けた時に与える攻撃力30%のダメージ' },
];

export default detailedRulesData;
